import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { buildWaiverSections } from '../components/waiver/waiverSections';
import { WaiverCheck } from '../components/waiver/WaiverCheck';
import { getRecaptchaToken } from '../lib/recaptcha';
import { 
  formatPhoneNumber, 
  formatName,
  formatState,
  formatZipCode,
  validatePhone, 
  validateEmail, 
  validateName,
  validateDOB,
  validateMinorDOB,
  validateState,
  validateZipCode,
  validateCity,
  validateStreetAddress
} from '../lib/validation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WaiverPage = () => {
  const navigate = useNavigate();
  const sigPadRef = useRef(null);
  const [honeypot, setHoneypot] = useState('');
  const sectionRef = useRef(null); // Ref for scrolling to section top on mobile
  const formRef = useRef(null); // Ref for scrolling to form after check
  
  // Waiver check state
  
  // Form state
  const [signingFor, setSigningFor] = useState(null); // 'myself' or 'minor'
  const [mediaConsent, setMediaConsent] = useState(null); // true, false, or null
  const [formData, setFormData] = useState({
    // Participant info (myself)
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    // Minor info
    minorFirstName: '',
    minorLastName: '',
    minorDOB: '',
    // Parent/Guardian info (for minor)
    parentFirstName: '',
    parentLastName: '',
    parentEmail: '',
    parentPhone: '',
    parentStreetAddress: '',
    parentCity: '',
    parentState: '',
    parentZipCode: '',
  });
  
  // Section initials
  const [initials, setInitials] = useState({
    section2: '',
    section3: '',
    section5: '',
    section11: ''
  });
  
  // Signer confirmation for minor waivers
  const [signerIsParent, setSignerIsParent] = useState(null); // true, false, or null
  const [signerInfo, setSignerInfo] = useState({
    signerName: '',
    signerRelationship: ''
  });
  
  // UI state
  const [currentSection, setCurrentSection] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  
  // Field-level validation errors (shown inline under each field)
  const [fieldErrors, setFieldErrors] = useState({});

  // Check if user is logged in
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (localStorage.getItem('isAuthenticated') === 'true' && user) {
      const userData = JSON.parse(user);
      // Pre-fill email from logged in user
      setFormData(prev => ({ ...prev, email: userData.email }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format phone fields
    if (name === 'phone' || name === 'emergencyContactPhone' || name === 'parentPhone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
      // Validate after formatting
      const validation = validatePhone(formatted);
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
      return;
    }
    
    // Format and validate name fields
    if (['firstName', 'lastName', 'minorFirstName', 'minorLastName', 
         'parentFirstName', 'parentLastName', 'emergencyContactName'].includes(name)) {
      const formatted = formatName(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
      // Validate
      const label = name.includes('emergency') ? 'Emergency contact name' : 
                    name.includes('minor') ? "Minor's name" : 
                    name.includes('parent') ? "Parent's name" : 'Name';
      const validation = validateName(formatted, label);
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
      return;
    }
    
    // Format and validate state fields
    if (name === 'state' || name === 'parentState') {
      const formatted = formatState(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
      const validation = validateState(formatted);
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
      return;
    }
    
    // Format and validate zip code fields
    if (name === 'zipCode' || name === 'parentZipCode') {
      const formatted = formatZipCode(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
      const validation = validateZipCode(formatted);
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
      return;
    }
    
    // Validate email fields
    if (name === 'email' || name === 'parentEmail') {
      setFormData(prev => ({ ...prev, [name]: value }));
      const validation = validateEmail(value);
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
      return;
    }
    
    // Validate city fields
    if (name === 'city' || name === 'parentCity') {
      setFormData(prev => ({ ...prev, [name]: value }));
      const validation = validateCity(value);
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
      return;
    }
    
    // Validate street address fields
    if (name === 'streetAddress' || name === 'parentStreetAddress') {
      setFormData(prev => ({ ...prev, [name]: value }));
      const validation = validateStreetAddress(value);
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
      return;
    }
    
    // Validate minor DOB
    if (name === 'minorDOB') {
      setFormData(prev => ({ ...prev, [name]: value }));
      const validation = validateMinorDOB(value);
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
      return;
    }
    
    // Validate adult DOB (must be 18+)
    if (name === 'dateOfBirth') {
      setFormData(prev => ({ ...prev, [name]: value }));
      const validation = validateDOB(value, 18, 120);
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInitialChange = (section, value) => {
    // Only allow 2-3 characters for initials
    if (value.length <= 3) {
      setInitials(prev => ({ ...prev, [section]: value.toUpperCase() }));
    }
  };

  const clearSignature = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setSignatureData(null);
    }
  };

  const saveSignature = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      const dataUrl = sigPadRef.current.toDataURL('image/png');
      setSignatureData(dataUrl);
      return dataUrl;
    }
    return null;
  };

  const validateSection = (sectionIndex) => {
    if (sectionIndex === 0) {
      return signingFor !== null;
    }
    if (sectionIndex === 1) {
      if (signingFor === 'myself') {
        // Check all required fields are filled AND valid
        const checks = [
          { value: formData.firstName, validator: () => validateName(formData.firstName, 'First name') },
          { value: formData.lastName, validator: () => validateName(formData.lastName, 'Last name') },
          { value: formData.dateOfBirth, validator: () => validateDOB(formData.dateOfBirth, 18, 120) },
          { value: formData.email, validator: () => validateEmail(formData.email) },
          { value: formData.phone, validator: () => validatePhone(formData.phone) },
          { value: formData.streetAddress, validator: () => validateStreetAddress(formData.streetAddress) },
          { value: formData.city, validator: () => validateCity(formData.city) },
          { value: formData.state, validator: () => validateState(formData.state) },
          { value: formData.zipCode, validator: () => validateZipCode(formData.zipCode) },
          { value: formData.emergencyContactName, validator: () => validateName(formData.emergencyContactName, 'Emergency contact name') },
          { value: formData.emergencyContactPhone, validator: () => validatePhone(formData.emergencyContactPhone) },
        ];
        
        return checks.every(check => check.value && check.validator().isValid);
      } else {
        // Minor flow
        const checks = [
          { value: formData.minorFirstName, validator: () => validateName(formData.minorFirstName, "Minor's first name") },
          { value: formData.minorLastName, validator: () => validateName(formData.minorLastName, "Minor's last name") },
          { value: formData.minorDOB, validator: () => validateMinorDOB(formData.minorDOB) },
          { value: formData.parentFirstName, validator: () => validateName(formData.parentFirstName, "Parent's first name") },
          { value: formData.parentLastName, validator: () => validateName(formData.parentLastName, "Parent's last name") },
          { value: formData.parentEmail, validator: () => validateEmail(formData.parentEmail) },
          { value: formData.parentPhone, validator: () => validatePhone(formData.parentPhone) },
          { value: formData.parentStreetAddress, validator: () => validateStreetAddress(formData.parentStreetAddress) },
          { value: formData.parentCity, validator: () => validateCity(formData.parentCity) },
          { value: formData.parentState, validator: () => validateState(formData.parentState) },
          { value: formData.parentZipCode, validator: () => validateZipCode(formData.parentZipCode) },
        ];
        
        return checks.every(check => check.value && check.validator().isValid);
      }
    }
    if (sectionIndex === 2) {
      return initials.section2.length >= 2;
    }
    if (sectionIndex === 3) {
      return initials.section3.length >= 2;
    }
    if (sectionIndex === 4) {
      return initials.section5.length >= 2;
    }
    if (sectionIndex === 5) {
      return initials.section11.length >= 2;
    }
    if (sectionIndex === 6) {
      return mediaConsent !== null; // Must choose yes or no
    }
    return true;
  };
  
  // Get specific error message for section validation
  const getSectionValidationErrors = () => {
    if (currentSection !== 1) return null;
    
    const errors = [];
    
    if (signingFor === 'myself') {
      if (!validateName(formData.firstName, 'First name').isValid) errors.push('First name');
      if (!validateName(formData.lastName, 'Last name').isValid) errors.push('Last name');
      if (!validateDOB(formData.dateOfBirth, 18, 120).isValid) errors.push('Date of birth');
      if (!validateEmail(formData.email).isValid) errors.push('Email');
      if (!validatePhone(formData.phone).isValid) errors.push('Phone');
      if (!validateStreetAddress(formData.streetAddress).isValid) errors.push('Street address');
      if (!validateCity(formData.city).isValid) errors.push('City');
      if (!validateState(formData.state).isValid) errors.push('State');
      if (!validateZipCode(formData.zipCode).isValid) errors.push('ZIP code');
      if (!validateName(formData.emergencyContactName, 'Name').isValid) errors.push('Emergency contact name');
      if (!validatePhone(formData.emergencyContactPhone).isValid) errors.push('Emergency contact phone');
    } else {
      if (!validateName(formData.minorFirstName, 'Name').isValid) errors.push("Minor's first name");
      if (!validateName(formData.minorLastName, 'Name').isValid) errors.push("Minor's last name");
      if (!validateMinorDOB(formData.minorDOB).isValid) errors.push("Minor's date of birth");
      if (!validateName(formData.parentFirstName, 'Name').isValid) errors.push("Parent's first name");
      if (!validateName(formData.parentLastName, 'Name').isValid) errors.push("Parent's last name");
      if (!validateEmail(formData.parentEmail).isValid) errors.push("Parent's email");
      if (!validatePhone(formData.parentPhone).isValid) errors.push("Parent's phone");
      if (!validateStreetAddress(formData.parentStreetAddress).isValid) errors.push("Street address");
      if (!validateCity(formData.parentCity).isValid) errors.push("City");
      if (!validateState(formData.parentState).isValid) errors.push("State");
      if (!validateZipCode(formData.parentZipCode).isValid) errors.push("ZIP code");
    }
    
    return errors.length > 0 ? errors : null;
  };

  // Track if user has started interacting with the form
  const [hasStartedForm, setHasStartedForm] = useState(false);

  // Scroll to section top after section changes (mobile-optimized)
  // Only scroll after user has started interacting with the form
  useEffect(() => {
    // Don't scroll on initial page load
    if (!hasStartedForm) return;
    
    // Small delay to let the DOM update
    const timer = setTimeout(() => {
      // On mobile, scroll to the very top of the section content
      if (sectionRef.current) {
        const isMobile = window.innerWidth <= 768;
        // More generous offset on mobile to ensure title is visible
        const headerOffset = isMobile ? 120 : 100;
        const element = sectionRef.current;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth'
        });
      } else {
        // Fallback: scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 150); // Slightly longer delay for DOM to settle
    return () => clearTimeout(timer);
  }, [currentSection, hasStartedForm]);

  const nextSection = () => {
    if (validateSection(currentSection)) {
      setHasStartedForm(true);
      setCurrentSection(prev => prev + 1);
      setError('');
    } else {
      // Get specific errors for better user feedback
      const validationErrors = getSectionValidationErrors();
      if (validationErrors && validationErrors.length > 0) {
        setError(`Please fix the following fields: ${validationErrors.join(', ')}`);
        // Scroll to error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError('Please complete all required fields before continuing.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const prevSection = () => {
    setHasStartedForm(true);
    setCurrentSection(prev => Math.max(0, prev - 1));
    setError('');
  };

  const handleSubmit = async () => {
    // Validate signature first
    const signature = saveSignature();
    if (!signature) {
      setError('Please provide your signature to complete the waiver.');
      return;
    }

    // Pre-submission validation - check all required fields are present and valid
    const missingFields = [];
    
    if (signingFor === 'myself') {
      if (!formData.firstName?.trim()) missingFields.push('First Name');
      if (!formData.lastName?.trim()) missingFields.push('Last Name');
      if (!formData.dateOfBirth?.trim()) missingFields.push('Date of Birth');
      if (!formData.email?.trim()) missingFields.push('Email');
      if (!formData.phone?.trim()) missingFields.push('Phone');
      if (!formData.streetAddress?.trim()) missingFields.push('Street Address');
      if (!formData.city?.trim()) missingFields.push('City');
      if (!formData.state?.trim()) missingFields.push('State');
      if (!formData.zipCode?.trim()) missingFields.push('ZIP Code');
      if (!formData.emergencyContactName?.trim()) missingFields.push('Emergency Contact Name');
      if (!formData.emergencyContactPhone?.trim()) missingFields.push('Emergency Contact Phone');
    } else {
      if (!formData.minorFirstName?.trim()) missingFields.push("Minor's First Name");
      if (!formData.minorLastName?.trim()) missingFields.push("Minor's Last Name");
      if (!formData.minorDOB?.trim()) missingFields.push("Minor's Date of Birth");
      if (!formData.parentFirstName?.trim()) missingFields.push("Parent's First Name");
      if (!formData.parentLastName?.trim()) missingFields.push("Parent's Last Name");
      if (!formData.parentEmail?.trim()) missingFields.push("Parent's Email");
      if (!formData.parentPhone?.trim()) missingFields.push("Parent's Phone");
      if (!formData.parentStreetAddress?.trim()) missingFields.push('Street Address');
      if (!formData.parentCity?.trim()) missingFields.push('City');
      if (!formData.parentState?.trim()) missingFields.push('State');
      if (!formData.parentZipCode?.trim()) missingFields.push('ZIP Code');
      
      // Validate signer confirmation for minor waivers
      if (signerIsParent === null) {
        missingFields.push('Signer Confirmation');
      } else if (signerIsParent === false) {
        if (!signerInfo.signerName?.trim()) missingFields.push('Signer Name');
        if (!signerInfo.signerRelationship) missingFields.push('Signer Relationship');
      }
    }
    
    // Check initials
    if (!initials.section2 || initials.section2.length < 2) missingFields.push('Section 2 Initials');
    if (!initials.section3 || initials.section3.length < 2) missingFields.push('Section 3 Initials');
    if (!initials.section5 || initials.section5.length < 2) missingFields.push('Sections 4-5 Initials');
    if (!initials.section11 || initials.section11.length < 2) missingFields.push('Health Requirements Initials');
    
    // Check media consent
    if (mediaConsent === null) missingFields.push('Media Consent Selection');
    
    if (missingFields.length > 0) {
      setError(`Missing required information: ${missingFields.join(', ')}. Please go back and complete all fields.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Determine actual signer info for minor waivers
      let actualSignerName = null;
      let actualSignerRelationship = null;
      
      if (signingFor === 'minor') {
        if (signerIsParent) {
          actualSignerName = `${formData.parentFirstName.trim()} ${formData.parentLastName.trim()}`;
          actualSignerRelationship = 'Parent/Guardian';
        } else {
          actualSignerName = signerInfo.signerName.trim();
          actualSignerRelationship = signerInfo.signerRelationship;
        }
      }

      const waiverData = {
        signing_for: signingFor,
        participant: signingFor === 'myself' ? {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          date_of_birth: formData.dateOfBirth.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          street_address: formData.streetAddress.trim(),
          city: formData.city.trim(),
          state: formData.state.trim().toUpperCase(),
          zip_code: formData.zipCode.trim(),
          emergency_contact_name: formData.emergencyContactName.trim(),
          emergency_contact_phone: formData.emergencyContactPhone.trim()
        } : {
          minor_first_name: formData.minorFirstName.trim(),
          minor_last_name: formData.minorLastName.trim(),
          minor_dob: formData.minorDOB.trim(),
          parent_first_name: formData.parentFirstName.trim(),
          parent_last_name: formData.parentLastName.trim(),
          parent_email: formData.parentEmail.trim().toLowerCase(),
          parent_phone: formData.parentPhone.trim(),
          parent_street_address: formData.parentStreetAddress.trim(),
          parent_city: formData.parentCity.trim(),
          parent_state: formData.parentState.trim().toUpperCase(),
          parent_zip_code: formData.parentZipCode.trim(),
          // Signer info (who actually signed)
          signer_is_parent: signerIsParent,
          actual_signer_name: actualSignerName,
          actual_signer_relationship: actualSignerRelationship
        },
        initials: {
          section2: initials.section2.toUpperCase(),
          section3: initials.section3.toUpperCase(),
          section5: initials.section5.toUpperCase(),
          section11: initials.section11.toUpperCase()
        },
        media_consent: mediaConsent,
        signature_data: signature,
        signed_at: new Date().toISOString(),
        website: honeypot,
        recaptcha_token: await getRecaptchaToken('waiver_form')
      };

      const headers = { 
        'Content-Type': 'application/json'
      };

      const response = await axios.post(`${BACKEND_URL}/api/waiver/sign`, waiverData, { 
        headers,
        withCredentials: true,
        validateStatus: (status) => status < 500 // Don't throw for 4xx errors
      });
      
      // Handle different response statuses
      if (response.status >= 200 && response.status < 300) {
        // Success - show success screen first
        setSuccess(true);
      } else if (response.status === 400) {
        // Bad request - validation error from backend
        const errorMsg = response.data?.detail || 'Please check your information and try again.';
        setError(`Submission error: ${errorMsg}`);
      } else if (response.status === 401) {
        // Unauthorized
        setError('Your session has expired. Please refresh the page and try again.');
      } else if (response.status === 422) {
        // Validation error
        const errorDetail = response.data?.detail;
        if (Array.isArray(errorDetail)) {
          const fieldErrors = errorDetail.map(e => e.loc?.[1] || e.msg).join(', ');
          setError(`Invalid data: ${fieldErrors}`);
        } else {
          setError('Please check your information and try again.');
        }
      } else {
        // Other 4xx errors
        setError('Unable to submit waiver. Please try again or contact support.');
      }
    } catch (err) {
      // Network errors or 5xx server errors
      console.error('Waiver submission error:', err);
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      } else if (err.code === 'ERR_NETWORK' || !navigator.onLine) {
        setError('Network error. Please check your internet connection and try again.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again in a few moments. If this persists, contact support.');
      } else {
        setError(err.response?.data?.detail || 'Failed to submit waiver. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Scroll to form section
  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Success screen
  if (success) {
    const redirectUrl = signingFor === 'myself' 
      ? '/book-bungee'
      : '/book-aerial';
    const buttonText = signingFor === 'myself'
      ? 'Continue to Book Bungee Class'
      : 'Continue to Book Aerial Class';
      
    return (
      <div className="waiver-page">
        <div className="waiver-container">
          <Card className="waiver-success-card">
            <CardContent className="waiver-success-content">
              <CheckCircle2 size={72} className="success-icon" />
              <h2>Waiver Signed Successfully!</h2>
              <p>Thank you for completing the CAL FIT waiver.</p>
              <div className="success-details">
                <p>A confirmation has been sent to:</p>
                <p className="email-highlight">
                  {signingFor === 'myself' ? formData.email : formData.parentEmail}
                </p>
              </div>
              <Button 
                onClick={() => window.location.href = redirectUrl}
                className="waiver-continue-btn"
                data-testid="waiver-continue-btn"
              >
                {buttonText}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const sections = buildWaiverSections({
    formData,
    handleChange,
    fieldErrors,
    initials,
    handleInitialChange,
    signingFor,
    setSigningFor,
    mediaConsent,
    setMediaConsent,
    signerIsParent,
    setSignerIsParent,
    signerInfo,
    setSignerInfo,
    sigPadRef,
    clearSignature
  });

  const totalSections = sections.length;
  const progress = ((currentSection + 1) / totalSections) * 100;

  return (
    <div className="waiver-page">
      <div className="waiver-container">
        <div className="waiver-header">
          <h1>CAL FIT Waiver & Media Release</h1>
          <p>Release and Waiver of Liability Agreement</p>
        </div>

        {/* Waiver Status Check Section - Always visible */}
        <WaiverCheck
          onPrefillEmail={(email) => setFormData(prev => ({ ...prev, email }))}
          onGoToForm={scrollToForm}
        />

        {/* Form Section */}
        <div ref={formRef} className="waiver-form-section">
          {/* Honeypot — hidden from real users, bots auto-fill it */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            className="hp-field"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          <div className="waiver-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="progress-text">Step {currentSection + 1} of {totalSections}</span>
          </div>

        <Card className="waiver-card">
          <CardContent className="waiver-content">
            <h2 className="section-title" ref={sectionRef}>{sections[currentSection].title}</h2>
            
            {sections[currentSection].content}

            {error && (
              <div className="waiver-error">
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            <div className="waiver-navigation">
              {currentSection > 0 && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={prevSection}
                  className="nav-btn prev-btn"
                >
                  Previous
                </Button>
              )}
              
              {currentSection < totalSections - 1 ? (
                <Button 
                  type="button" 
                  onClick={nextSection}
                  className="nav-btn next-btn"
                  disabled={!validateSection(currentSection)}
                >
                  Continue
                </Button>
              ) : (
                <Button 
                  type="button" 
                  onClick={handleSubmit}
                  className="nav-btn submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Sign & Submit Waiver'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        </div> {/* Close waiver-form-section */}
      </div>
    </div>
  );
};

export default WaiverPage;
